import type { ReactNode } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/hooks/useI18n";

interface DetailSheetTabsProps {
  /** KPI, grafici e riepiloghi: la lettura d'insieme. */
  stats: ReactNode;
  /** La tabella degli itinerari con le rate annidate. */
  itineraries: ReactNode;
  /** Numero di viaggi, accanto all'etichetta della seconda tab. */
  itinerariesCount: number;
}

/**
 * Le due tab delle schede cliente e consulente.
 *
 * I dati arrivano da una sola RPC per scheda e le tab li dividono solo a video: KPI e
 * tabella nascono dalle stesse rate, e spezzarle in due chiamate vorrebbe dire tenere
 * allineate due copie dello stesso calcolo. Stesso motivo per cui l'export del dossier
 * riceve il payload intero e non la tab aperta.
 *
 * Tab non controllate: il pannello si smonta alla chiusura, quindi ogni scheda si riapre
 * sulle statistiche.
 */
export function DetailSheetTabs({
  stats,
  itineraries,
  itinerariesCount,
}: DetailSheetTabsProps) {
  const { t } = useI18n();
  const triggerClass =
    "gap-1.5 rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 text-gray-500 shadow-none transition-colors hover:text-gray-700 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-primary data-[state=active]:shadow-none";

  return (
    <Tabs defaultValue="stats">
      <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-gray-200 bg-transparent p-0">
        <TabsTrigger value="stats" className={triggerClass}>
          {t("dashboard.overview.detail.tabStats")}
        </TabsTrigger>
        <TabsTrigger value="itineraries" className={triggerClass}>
          {t("dashboard.overview.detail.tabItineraries")}
          <span className="text-xs text-gray-400 tabular-nums">{itinerariesCount}</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="stats" className="space-y-4 pt-2">
        {stats}
      </TabsContent>
      <TabsContent value="itineraries" className="pt-2">
        {itineraries}
      </TabsContent>
    </Tabs>
  );
}
